
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import { Heart, Calendar, Users, X, ClipboardList, Sparkles, ArrowLeft, ArrowRight } from "lucide-react";
import discoverImage from "@/assets/Discover4x-4.png.asset.json";
import connectImage from "@/assets/Interested4x-4.png.asset.json";
import playdateImage from "@/assets/Going4x-8.png.asset.json";
import meetupImage from "@/assets/Organise_a_meetup3x-2.png.asset.json";
import noticeboardImage from "@/assets/Noticeboard3x-2.png.asset.json";
import phoneHomeScreen from "@/assets/home-screen-phone.webp";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { VenueStoryPreview } from "@/components/VenueStoryPreview";
import { StoriesShowcase } from "@/components/StoriesShowcase";

import { HeroMedia } from "@/components/HeroMedia";
import { LatestActivitiesSection } from "@/components/LatestActivitiesSection";
import { MeetLocalsSection } from "@/components/MeetLocalsSection";
import { OpenToMeetStrip } from "@/components/OpenToMeetStrip";

import { PlayDatePromo } from "@/components/PlayDatePromo";
import { FeedbackCard } from "@/components/FeedbackCard";
import { isMedianApp } from "@/lib/medianOneSignal";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useEffect, useState } from "react";

const Index = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [showAppPrompt, setShowAppPrompt] = useState(true);
  const [redirectChecked, setRedirectChecked] = useState(false);

  // Returning visitors land on /activities; first-timers see the homepage.
  // Logo clicks within the same session bypass the redirect (sessionStorage flag).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("home") === "1") {
      sessionStorage.setItem("homeRedirectSkipped", "true");
      setRedirectChecked(true);
      return;
    }
    const hasVisited = localStorage.getItem("hasVisitedHome") === "true";
    const skippedThisSession =
      sessionStorage.getItem("homeRedirectSkipped") === "true";
    if (hasVisited && !skippedThisSession) {
      sessionStorage.setItem("homeRedirectSkipped", "true");
      navigate("/activities", { replace: true });
      return;
    }
    localStorage.setItem("hasVisitedHome", "true");
    sessionStorage.setItem("homeRedirectSkipped", "true");
    setRedirectChecked(true);
  }, [location.search, navigate]);

  useEffect(() => {
    setShowAppPrompt(localStorage.getItem("getTheAppDismissed") !== "true");
  }, []);

  const handleDismissAppPrompt = () => {
    localStorage.setItem("getTheAppDismissed", "true");
    setShowAppPrompt(false);
  };

  useEffect(() => {
    const state = location.state as { scrollTo?: string } | null;
    if (state?.scrollTo) {
      setTimeout(() => {
        document.getElementById(state.scrollTo!)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      window.history.replaceState({}, '');
    }
  }, [location.state]);
  if (!redirectChecked) return null;
  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Balham Babes — Baby & Toddler Activities, Classes, Soft Play"
        description="Find baby and toddler classes, playgroups, soft play and baby-friendly cafés in Balham — see who's going and organise a meetup with local parents and carers."
        path="/"
      />
      {/* Hero Section */}
      <div
        className="relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #6D3263 0%, #DF6283 100%)" }}
      >
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="space-y-4">
                <Badge className="bg-light-fuschia text-foreground border-0">Balham, Clapham, Tooting & nearby</Badge>
                <h1 className="text-4xl sm:text-5xl md:text-5xl font-bold text-white leading-tight">
                  South West London with babes,
                  <span className="text-white"> SORTED.</span>
                </h1>
                <p className="text-lg text-white/90 leading-relaxed">
                  A{" "}
                  <button
                    type="button"
                    onClick={() => document.getElementById("get-the-app")?.scrollIntoView({ behavior: "smooth" })}
                    className="underline underline-offset-2 hover:text-white/80 font-semibold text-white"
                  >
                    web-app
                  </button>
                  {" "}to discover baby and toddler activities in South West London, and connect with local mums, dads, nannies and childminders for easy playdates
                </p>
              </div>



              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  size="lg"
                  className="border-2 border-white shadow-md hover:shadow-lg transition-all w-full sm:w-auto h-14 px-8 py-0 text-base font-semibold bg-white hover:bg-white/90 text-[#6D3263] hover:text-[#6D3263]"
                  onClick={() => navigate("/auth?mode=signup")}
                >
                  <Heart className="mr-2 h-5 w-5" />
                  Get started free
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-white bg-transparent hover:bg-white/10 text-white hover:text-white shadow-md hover:shadow-lg transition-all w-full sm:w-auto h-14 px-8 py-0 text-base font-semibold"
                  onClick={() => navigate("/activities")}
                >
                  Explore Activities
                </Button>
              </div>
            </div>

            <div className="relative">
              <HeroMedia />
              <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-4 shadow-card border-0">
                <div className="text-center">
                  <div className="text-2xl font-bold text-bright-fuschia">35+</div>
                  <div className="text-sm text-muted-foreground">Daily Plans</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* How It Works Section */}
      <HowItWorksCarousel
        user={user}
        onJoin={() => navigate("/auth?mode=signup")}
      />

      {/* Venue Story of the Week (sponsored) - visibility controlled by is_public flag in the component */}
      <VenueStoryPreview />

      {/* Just Added — newest community activities */}
      <LatestActivitiesSection />

      {/* Open to Meet strip */}
      <OpenToMeetStrip homepage onSeeAll={() => navigate("/playdates?tab=friends")} />

      {/* Meet local parents & carers — Open to Meet + upcoming playdates */}
      <MeetLocalsSection />
      {/* Local stories showcase */}
      <StoriesShowcase />




      {/* Play Date Promo - hidden until feature is open to all */}
      {/* <PlayDatePromo /> */}

      {/* Get the app experience — Add to Home Screen (web only) */}
      {!isMedianApp() && (
      <section id="get-the-app" className="bg-gradient-to-br from-light-fuschia/60 via-card to-primary/5 border-t border-border/40 py-16 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-10 items-center">
            {/* Phone image */}
            <div className="flex justify-center md:justify-end order-1 md:col-span-2">
              <img
                src={phoneHomeScreen}
                alt="Balham Babes app on a phone home screen"
                width={1024}
                height={1024}
                loading="lazy"
                className="w-full max-w-[320px] sm:max-w-[380px] md:max-w-[420px] h-auto drop-shadow-2xl"
              />
            </div>

            {/* Copy */}
            <div className="text-center md:text-left order-2 md:col-span-3">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold mb-4">
                📱 Get the full app experience
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4 leading-tight">
                Add Balham Babes to your home screen
              </h2>
              <p className="text-muted-foreground text-base sm:text-lg mb-6 max-w-lg mx-auto md:mx-0">
                One tap from your home screen, just like a real app — and turn on push notifications so you never miss when a friend is going to an activity, sends you a message, or starts a casual playdate nearby.
              </p>
              <ul className="text-sm text-muted-foreground space-y-2 mb-8 max-w-md mx-auto md:mx-0 text-left">
                <li className="flex items-start gap-2">
                  <span className="text-primary font-bold mt-0.5">✓</span>
                  <span>Instant access — no app store needed</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary font-bold mt-0.5">✓</span>
                  <span>Push notifications for messages and new playdates</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary font-bold mt-0.5">✓</span>
                  <span>Free to join — works on iPhone and Android</span>
                </li>
              </ul>
              <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                <a
                  href="https://youtube.com/shorts/US7c-XmJ2p4?si=Q5lu_oJHHc_OvXwQ"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground px-5 py-3 text-base font-semibold shadow-md hover:shadow-lg transition-all"
                >
                  <span>🍎</span> iPhone / iPad tutorial
                </a>
                <a
                  href="https://youtube.com/shorts/gZTEWw2dGVw?si=rVOZz03zkYkA0CKx"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-md border-2 border-primary text-primary hover:bg-primary/10 px-5 py-3 text-base font-semibold transition-all"
                >
                  <span>🤖</span> Android tutorial
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Final sign-up CTA */}
      {!user && (
        <section className="relative overflow-hidden bg-gradient-to-br from-primary via-primary to-bright-fuschia text-white py-20 border-t border-border/40">
          <div className="absolute inset-0 opacity-20 pointer-events-none">
            <div className="absolute -top-20 -left-20 w-80 h-80 rounded-full bg-white/30 blur-3xl" />
            <div className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-light-fuschia/40 blur-3xl" />
          </div>
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm text-xs font-semibold mb-5">
              <Sparkles className="h-3.5 w-3.5" />
              Free to join — join your neighbours
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-5 leading-tight">
              Your village is waiting.
            </h2>
            <p className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto mb-8 leading-relaxed">
              Discover local activities, meet parents and carers nearby, and plan playdates that actually happen. It only takes a minute to sign up.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <Button
                size="lg"
                onClick={() => navigate("/auth?mode=signup")}
                className="bg-white text-primary hover:bg-white/90 font-semibold shadow-lg px-8 text-base"
              >
                <Heart className="mr-2 h-5 w-5" />
                Create your free account
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate("/activities")}
                className="border-2 border-white text-white bg-transparent hover:bg-white/10 px-8 text-base"
              >
                Browse activities first
              </Button>
            </div>
            <p className="text-xs text-white/70 mt-6">
              No spam, ever. Just real local families and things to do.
            </p>
          </div>
        </section>
      )}

      {/* Feedback Section */}
      <section className="border-t border-border/40 py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <FeedbackCard />
        </div>
      </section>

      <Footer />
    </div>
  );
};

type Step = {
  eyebrow: string;
  title: string;
  body: string;
  image: string;
  alt: string;
  link: string;
  linkLabel: string;
};

const STEPS: Step[] = [
  {
    eyebrow: "Browse",
    title: "Discover what's on near you",
    body:
      "Browse activities, classes and baby-friendly cafes, all organised by day so you can plan ahead.",
    image: discoverImage.url,
    alt: "Discover baby and toddler activities by day",
    link: "/activities",
    linkLabel: "Explore activities",
  },
  {
    eyebrow: "Connect",
    title: "Show your interest",
    body:
      "Tap the \"Interested\" button on activities you'd like to go to. Other parents and carers will see you're open to meet there — and they might just say hello!",
    image: connectImage.url,
    alt: "Show your interest on activities to meet other parents and carers",
    link: "/interested",
    linkLabel: "See who's interested",
  },
  {
    eyebrow: "Join in",
    title: "Start or join casual play dates",
    body:
      "Heading there? Tap \"Going\" to start or join a casual play date, or message other parents and carers directly to plan something together.",
    image: playdateImage.url,
    alt: "Start or join a casual play date",
    link: "/playdates",
    linkLabel: "Find play dates",
  },
  {
    eyebrow: "Gather",
    title: "Organise a meetup",
    body:
      "Pick a park, a café or a class, invite friends, and use the built-in group chat to sort the details together.",
    image: meetupImage.url,
    alt: "Organise a meetup with other local parents and carers",
    link: "/create-play-date",
    linkLabel: "Plan a meetup",
  },
  {
    eyebrow: "Local finds",
    title: "Check the noticeboard",
    body:
      "Browse classifieds, baby-sitting offers, lost and found, family services and announcements from locals — all in one place.",
    image: noticeboardImage.url,
    alt: "Check the local noticeboard for listings and tips",
    link: "/noticeboard",
    linkLabel: "Browse the noticeboard",
  },
];

const HowItWorksCarousel = ({
  user,
  onJoin,
}: {
  user: unknown;
  onJoin: () => void;
}) => {
  const [api, setApi] = useState<CarouselApi | null>(null);
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    if (!api) return;
    const update = () => {
      setSelected(api.selectedScrollSnap());
    };
    update();
    api.on("select", update);
    api.on("reInit", update);
    return () => {
      api.off("select", update);
    };
  }, [api]);

  return (
    <section className="bg-background border-t border-border/40 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-left mb-10">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-primary mb-3">
            <Sparkles className="h-4 w-4" />
            Your local village, in your pocket
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground leading-tight">
            Everything you need to find your people nearby
          </h2>
          <p className="text-muted-foreground mt-3 text-base sm:text-lg">
            Free to join. Built by local parents and carers, for local parents and carers.
          </p>
        </div>

        <div className="relative">
          <Carousel
            setApi={setApi}
            opts={{ align: "center", loop: true, dragFree: false }}
            className="w-full"
          >
            <CarouselContent className="-ml-4 py-5">
              {STEPS.map((step, index) => {
                const isActive = index === selected;
                return (
                  <CarouselItem
                    key={step.title}
                    className="pl-4 basis-[90%] sm:basis-[60%] lg:basis-[45%]"
                  >
                    <article
                      className={cn(
                        "h-full bg-card rounded-2xl p-5 border-2 border-primary/20 shadow-card text-center flex flex-col transition-all duration-500 ease-out origin-center",
                        isActive
                          ? "scale-100 opacity-100 shadow-xl"
                          : "scale-90 opacity-50"
                      )}
                    >
                      <span className="text-xs font-semibold uppercase tracking-[0.16em] text-primary mb-2">
                        {step.eyebrow}
                      </span>
                      <h3 className="text-2xl sm:text-3xl font-bold text-foreground mb-3 leading-tight">
                        {step.title}
                      </h3>
                      <p className="text-muted-foreground text-sm">{step.body}</p>
                      <img
                        src={step.image}
                        alt={step.alt}
                        className="mt-5 w-full h-72 sm:h-96 object-contain"
                        loading="lazy"
                      />
                      <Link
                        to={step.link}
                        className="mt-auto pt-5 text-sm font-semibold text-primary hover:text-primary/80 transition-colors inline-flex items-center justify-center gap-1"
                      >
                        {step.linkLabel}
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </article>
                  </CarouselItem>
                );
              })}
            </CarouselContent>
          </Carousel>

          {/* Centered side arrows */}
          <button
            type="button"
            onClick={() => api?.scrollPrev()}
            aria-label="Previous"
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-10 h-12 w-12 rounded-full bg-card/95 backdrop-blur border border-border/60 shadow-lg flex items-center justify-center text-foreground transition hover:bg-primary hover:text-primary-foreground hover:scale-110"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => api?.scrollNext()}
            aria-label="Next"
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-10 h-12 w-12 rounded-full bg-card/95 backdrop-blur border border-border/60 shadow-lg flex items-center justify-center text-foreground transition hover:bg-primary hover:text-primary-foreground hover:scale-110"
          >
            <ArrowRight className="h-5 w-5" />
          </button>

          {/* Dots */}
          <div className="flex justify-center gap-1 mt-4">
            {STEPS.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Go to slide ${i + 1}`}
                onClick={() => api?.scrollTo(i)}
                className="h-11 w-11 flex items-center justify-center group"
              >
                <span
                  className={cn(
                    "block h-3 rounded-full transition-all duration-300",
                    i === selected ? "w-10 bg-primary" : "w-3 bg-primary/30 group-hover:bg-primary/50"
                  )}
                />
              </button>
            ))}
          </div>
        </div>

        {!user && (
          <div className="mt-10 flex justify-center">
            <Button
              size="lg"
              className="px-8 py-6 text-base font-semibold bg-primary hover:bg-primary/90 text-primary-foreground shadow-md hover:shadow-lg transition-all"
              onClick={onJoin}
            >
              <Heart className="mr-2 h-5 w-5" />
              Join Balham Babes
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default Index;

